import { defineStore } from "pinia";
import { useApiCall } from "~/composables/useApiCall";
import { useSearchStore } from "~/store/search";
import { useSearchFiltersStore } from "~/store/searchFilter";
import { useChartsStore } from "~/store/charts";
import { useOrgStore } from "~/store/org";
import { useUserStore } from "~/store/user";
import type { ReportStats } from "~/utils/ppt-reports/report";
import type RequestOptions from "~/types/RequestOptionsType";
import { requestQueryDateParser } from "~/utils/RequestQueryDateParser";
import type RequestQuery from "~/types/RequestQueryType";

interface PptReportState {
  isLoading: boolean;
  isGenerating: boolean;
  reportData: {
    searchData: any;
    chartData: any;
    orgData: any;
    logoUrl: string | null;
  };
  selectedSlides: string[];
  reportSettings: {
    title: string;
    dateRange: {
      start: Date;
      end: Date;
    };
  };
  generationProgress: {
    currentStep: string;
    totalSteps: number;
    currentStepNumber: number;
  };
  reportStats: ReportStats | null;
  topicChartImage: string | null;
  error: string | null;
}

/** Merge date-based arrays, removing duplicate at overlap point */
const mergeDateArrays = (first: any[], second: any[]): any[] => {
  if (!first?.length) return second || [];
  if (!second?.length) return first || [];
  const lastDate = first[first.length - 1]?.date;
  return [...first, ...second.filter((item) => item.date !== lastDate)];
};

/** Merge two raw API search results from split date ranges */
function mergeRawApiResponses(firstHalfData: any, secondHalfData: any): any {
  if (!firstHalfData) return secondHalfData;
  if (!secondHalfData) return firstHalfData;

  const firstCounts =
    firstHalfData.attributes?.search_result?.counts ||
    firstHalfData.attributes?.counts;
  const secondCounts =
    secondHalfData.attributes?.search_result?.counts ||
    secondHalfData.attributes?.counts;
  if (!firstCounts || !secondCounts) return firstHalfData || secondHalfData;

  // Merge all count arrays
  const countKeys = [
    "articles",
    "social",
    "reach",
    "article_sentiment",
    "facebook_shares",
    "pinterest_pins",
    "twitter_tweets",
  ];
  const mergedCounts = Object.fromEntries(
    countKeys
      .filter((k) => firstCounts[k] || secondCounts[k])
      .map((k) => [k, mergeDateArrays(firstCounts[k], secondCounts[k])]),
  );

  // Merge counts_by_media_type
  const firstByType =
    firstHalfData.attributes?.search_result?.counts_by_media_type ||
    firstHalfData.attributes?.counts_by_media_type;
  const secondByType =
    secondHalfData.attributes?.search_result?.counts_by_media_type ||
    secondHalfData.attributes?.counts_by_media_type;
  const mediaFields = [
    "articles",
    "reach",
    "article_sentiment",
    "social",
    "facebook_shares",
    "pinterest_pins",
  ];

  const mergedByType =
    firstByType || secondByType
      ? Object.fromEntries(
          [
            ...new Set([
              ...Object.keys(firstByType || {}),
              ...Object.keys(secondByType || {}),
            ]),
          ].map((type) => [
            type,
            Object.fromEntries(
              mediaFields
                .filter(
                  (f) => firstByType?.[type]?.[f] || secondByType?.[type]?.[f],
                )
                .map((f) => [
                  f,
                  mergeDateArrays(
                    firstByType?.[type]?.[f],
                    secondByType?.[type]?.[f],
                  ),
                ]),
            ),
          ]),
        )
      : undefined;

  return {
    ...firstHalfData,
    attributes: {
      ...firstHalfData.attributes,
      search_result: {
        ...(firstHalfData.attributes?.search_result || {}),
        counts: mergedCounts,
        counts_by_media_type: mergedByType,
      },
    },
  };
}

/**
 * Build an isolated search request without modifying global store state
 * This prevents UI updates during data collection
 */
function buildIsolatedSearchRequest(params: {
  searchType: string;
  searchText: string;
  profileId: string;
  dateRange: { start: Date; end: Date };
  querySearchType: number;
  overrides?: any;
}): RequestQuery {
  const searchStore = useSearchStore();
  const searchFiltersStore = useSearchFiltersStore();
  const userStore = useUserStore();
  const currentSearchFilter = searchFiltersStore.getCurrentSearchFilter;

  // Helper functions from useRequestQuery
  const addArticleSearchFields = (query: RequestQuery) => {
    query.data.id = null;
    query.data.attributes.limit = searchStore.limit;
    query.data.attributes.page = searchStore.page;
    query.data.attributes.sort = userStore.getMentionsMonitorSort;
  };

  const addChartSearchFields = (query: RequestQuery) => {
    query.data.attributes.aggregation_fields =
      currentSearchFilter.aggregation_fields;
    query.data.attributes.aggregation_scale =
      currentSearchFilter.aggregation_scale;
  };

  const getRelationships = () => {
    let relationshipObj = {};
    if (params.querySearchType !== 14) {
      relationshipObj = {
        profile: {
          data: {
            type: "profiles",
            id: params.profileId || searchStore.selectedNestedSearch.id,
          },
        },
      };
    }
    return relationshipObj;
  };

  // Build the request query using isolated parameters
  const RequestQuery: RequestQuery = {
    data: {
      attributes: {
        search_type: params.querySearchType,
        search_text: params.searchText || searchStore.searchText,
        filter_date_start: requestQueryDateParser(params.dateRange.start),
        filter_date_end: requestQueryDateParser(params.dateRange.end),
        filter_pagerank_min: currentSearchFilter.filter_pagerank_min,
        filter_pagerank_max: currentSearchFilter.filter_pagerank_max,
        filter_languages: currentSearchFilter.filter_languages,
        web_category: currentSearchFilter.web_category,
        print_category: currentSearchFilter.print_category,
        filter_countries: currentSearchFilter.filter_countries,
        filter_relevance: currentSearchFilter.filter_relevance,
        filter_article_sentiment: currentSearchFilter.filter_article_sentiment,
        filter_included_domains: searchFiltersStore.filterIncludedDomains,
        filter_excluded_domains: searchFiltersStore.filterExcludedDomains,
        filter_keywords: searchStore.filterKeywords,
        exclude_financial_news: currentSearchFilter.exclude_financial_news,
        exclude_press_releases: currentSearchFilter.exclude_press_releases,
        exclude_market_research: currentSearchFilter.exclude_market_research,
        show_only_subscribed_markets:
          currentSearchFilter.show_only_subscribed_markets,
        web_media_type: currentSearchFilter.web_media_type,
        print_media_type: currentSearchFilter.print_media_type,
        tv_media_type: currentSearchFilter.tv_media_type,
        radio_media_type: currentSearchFilter.radio_media_type,
      },
      relationships: getRelationships(),
      type: params.searchType,
    },
  };

  // Apply search type specific fields
  if (params.searchType === "chart-searches") {
    addChartSearchFields(RequestQuery);
  } else if (params.searchType === "csv-article-searches") {
    RequestQuery.data.attributes.limit = 1000;
    RequestQuery.data.attributes.page = 1;
    RequestQuery.data.attributes.search_type = 2;
    RequestQuery.data.attributes.sort = userStore.getMentionsMonitorSort;
  } else if (params.searchType === "topic-searches") {
    RequestQuery.data.attributes.aggregation_scale =
      currentSearchFilter.aggregation_scale;
    delete RequestQuery.data.attributes.search_type;
  } else {
    addArticleSearchFields(RequestQuery);
  }

  // Apply any overrides
  if (params.overrides) {
    Object.assign(RequestQuery.data.attributes, params.overrides);
  }

  return RequestQuery;
}

export const usePptReportsStore = defineStore("ppt-reports", {
  state: (): PptReportState => ({
    isLoading: false,
    isGenerating: false,
    reportData: {
      searchData: null,
      chartData: null,
      orgData: null,
      logoUrl: null,
    },
    selectedSlides: [], // Will be auto-populated based on available data
    reportSettings: {
      title: "Media Monitoring Report",
      dateRange: {
        start: new Date(new Date().setDate(new Date().getDate() - 30)),
        end: new Date(),
      },
    },
    generationProgress: {
      currentStep: "",
      totalSteps: 0,
      currentStepNumber: 0,
    },
    reportStats: null,
    topicChartImage: null,
    error: null,
  }),

  getters: {
    isReady: (state) => {
      return (
        state.reportData.searchData &&
        state.reportData.chartData &&
        state.reportData.orgData &&
        !state.isLoading
      );
    },

    hasLogo: (state) => {
      return !!state.reportData.logoUrl;
    },

    progressPercentage: (state) => {
      if (state.generationProgress.totalSteps === 0) return 0;
      return Math.round(
        (state.generationProgress.currentStepNumber /
          state.generationProgress.totalSteps) *
          100,
      );
    },

    hasValidStats: (state) => {
      return !!state.reportStats && Object.keys(state.reportStats).length > 0;
    },

    canShowPreview: (state) => {
      const hasStats =
        !!state.reportStats && Object.keys(state.reportStats).length > 0;
      return hasStats && state.selectedSlides.length > 0;
    },

    availableSlideTypes: () => [
      { id: "executive-summary", name: "Executive Summary", required: true },
      { id: "media-coverage", name: "Media Coverage & Reach", required: false },
      { id: "media-type", name: "Media Coverage by Type", required: false },
      { id: "media-impact", name: "Media Performance", required: false },
      {
        id: "competitive-analysis",
        name: "Competitive Analysis",
        required: false,
      },
      { id: "sentiment-analysis", name: "Sentiment Analysis", required: false },
      { id: "trending-topics", name: "Trending Topics", required: false },
      {
        id: "trending-topics-over-time",
        name: "Trending Topics Over Time",
        required: false,
      },
      { id: "most-amplified", name: "Social Amplification", required: false },
    ],
  },

  actions: {
    setReportTitle(title: string) {
      this.reportSettings.title = title;
    },

    setDateRange(start: Date, end: Date) {
      this.reportSettings.dateRange.start = start;
      this.reportSettings.dateRange.end = end;
    },

    determineAvailableSlides() {
      const orgStore = useOrgStore();
      const searchFilter = this.reportData.searchData?.searchFilter || {};

      // Start with required slides
      const slides: string[] = ["executive-summary"];

      // Media coverage - if we have chart data
      if (this.reportData.chartData?.dashboardChartSearches?.length > 0) {
        slides.push("media-coverage");
      }

      // Media type breakdown - if we have media type data
      if (
        this.reportData.chartData?.dashboardChartSearches?.[0]?.mediaTypeData
          ?.length > 0
      ) {
        slides.push("media-type");
      }

      // Media impact - if we have data for growth comparison
      if (this.reportData.chartData?.mainChartSearches?.length > 0) {
        slides.push("media-impact");
      }

      // Competitive analysis - if we have competitor data
      if (this.reportData.chartData?.competitorChartSearches?.length > 0) {
        slides.push("competitive-analysis");
      }

      // Sentiment analysis - if org has access AND we have sentiment data
      if (
        orgStore.hasArticleSentiment &&
        this.reportData.chartData?.mainChartSearches?.some(
          (search) => search.sentimentData?.length > 0,
        )
      ) {
        slides.push("sentiment-analysis");
      }

      // Trending topics - if we have topic data (both slides together)
      if (this.reportData.chartData?.topicSearch?.keywords?.length > 0) {
        slides.push("trending-topics");
        slides.push("trending-topics-over-time");
      }

      // Most amplified - if web media type is enabled (social data comes from web articles)
      if (searchFilter.web_media_type !== false) {
        slides.push("most-amplified");
      }

      this.selectedSlides = slides;
    },

    updateProgress(step: string, current: number, total: number) {
      this.generationProgress = {
        currentStep: step,
        currentStepNumber: current,
        totalSteps: total,
      };
    },

    clearError() {
      this.error = null;
    },

    setTopicChartImage(image: string | null) {
      this.topicChartImage = image;
    },

    async fetchReportData() {
      this.isLoading = true;
      this.error = null;

      try {
        const searchStore = useSearchStore();
        const chartsStore = useChartsStore();

        // Ensure we have a current search selected
        if (!searchStore.currentSelectedSearchDashboard) {
          return;
        }

        // Use date range from toolbar if available, otherwise use report settings
        const dateRange =
          chartsStore.selectedDateRange || this.reportSettings.dateRange;
        this.reportSettings.dateRange = dateRange;

        // Fetch all report data using proper API calls
        await this.fetchAllReportData(dateRange);

        // Fetch organization data
        await this.fetchOrgData();

        // Fetch logo if available
        await this.fetchOrgLogo();

        // Generate report statistics using ReportProcessor
        await this.generateReportStats();

        // Automatically determine which slides to include
        this.determineAvailableSlides();

        // Set default title based on search name
        if (searchStore.selectedSearch?.name) {
          this.reportSettings.title = `${searchStore.selectedSearch.name} - Media Report`;
        }
      } catch (error) {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to fetch report data";
        console.error("PPT Reports: Failed to fetch data", error);
      } finally {
        this.isLoading = false;
      }
    },

    async generateReportStats() {
      try {
        // Check if we have basic data before proceeding
        if (
          !this.reportData.searchData &&
          !this.reportData.chartData &&
          !this.reportData.orgData
        ) {
          return;
        }

        const { Report } = await import("~/utils/ppt-reports/report");

        const userStore = useUserStore();
        const orgStore = useOrgStore();

        const report = new Report({
          title: this.reportSettings.title,
          dashboardChartSearches:
            this.reportData.chartData.dashboardChartSearches || [],
          mainChartSearches: this.reportData.chartData.mainChartSearches || [],
          competitorChartSearches:
            this.reportData.chartData.competitorChartSearches || [],
          topicSearch: this.reportData.chartData.topicSearch || null,
          startDate: this.reportSettings.dateRange.start,
          endDate: this.reportSettings.dateRange.end,
          authorName: userStore.getUser?.attributes?.name || "Unknown",
          organizationName: orgStore.getOrg?.attributes?.name || "Organization",
          timeZone: userStore.getUser?.attributes?.time_zone || "UTC",
          currentDateFormat:
            userStore.getUser?.attributes?.date_format || "MM/DD/YYYY",
          currentLanguage: userStore.getUser?.attributes?.language || "en",
          selectedProfile: this.reportData.searchData?.selectedProfile,
          currentSearchDashboard: this.reportData.searchData?.dashboard,
          currentSearchFilter: {},
          organization: orgStore.getOrg,
          userNumberFormat: "en-US", // TODO: Get from user preferences
          amplifiedArticles: this.reportData.chartData.amplifiedArticles || [],
        });

        // Generate all statistical calculations using original Report class logic
        this.reportStats = report.reportStats;
      } catch (error) {
        console.warn("PPT Reports: Failed to generate statistics", error);
        // Don't throw error, just log warning - this allows the page to still load
      }
    },

    async fetchAllReportData(dateRange: { start: Date; end: Date }) {
      const searchStore = useSearchStore();
      const searchFiltersStore = useSearchFiltersStore();

      // Calculate comparison date ranges (split into two halves)
      const totalDays = Math.ceil(
        (dateRange.end.getTime() - dateRange.start.getTime()) /
          (1000 * 60 * 60 * 24),
      );
      const halfDays = Math.ceil(totalDays / 2);
      const midDate = new Date(
        dateRange.start.getTime() + halfDays * 24 * 60 * 60 * 1000,
      );

      const firstHalfDateRange = { start: dateRange.start, end: midDate };
      const secondHalfDateRange = { start: midDate, end: dateRange.end };

      // Determine aggregation scale based on date range
      const aggregationScale = totalDays > 120 ? "month" : "day";

      try {
        // Run chart searches first (they all use search_type: 1)
        const [
          dashboardChartSearches,
          mainChartSearches,
          competitorChartSearches,
          topicSearch,
        ] = await Promise.all([
          // Dashboard chart searches (current dashboard with all profiles)
          // Pass split date ranges to avoid API truncation issue
          this.fetchDashboardChartSearches(
            dateRange,
            firstHalfDateRange,
            secondHalfDateRange,
            aggregationScale,
          ),

          // Main chart searches (for growth comparisons)
          this.fetchMainChartSearches(
            firstHalfDateRange,
            secondHalfDateRange,
            aggregationScale,
          ),

          // Competitor chart searches (if comparison set)
          this.fetchCompetitorChartSearches(
            firstHalfDateRange,
            secondHalfDateRange,
            aggregationScale,
          ),

          // Topic search
          this.fetchTopicSearch(dateRange, aggregationScale),
        ]);

        // Run article searches separately to avoid race condition with querySearchType
        const articleSearches = await this.fetchArticleSearches(dateRange);

        // Store the fetched data
        this.reportData.searchData = {
          dashboard: searchStore.selectedSearch,
          profiles: searchStore.nestedSearches,
          selectedProfile: searchStore.selectedProfile,
          searchFilter: searchFiltersStore.getCurrentSearchFilter,
        };

        this.reportData.chartData = {
          dashboardChartSearches,
          mainChartSearches,
          competitorChartSearches,
          topicSearch,
          articleSearches,
        };
      } catch (error) {
        console.error("PPT Reports: Error fetching report data", error);
        throw error;
      }
    },

    async fetchDashboardChartSearches(
      fullDateRange: { start: Date; end: Date },
      firstHalf: { start: Date; end: Date },
      secondHalf: { start: Date; end: Date },
      aggregationScale: string,
    ) {
      const searchStore = useSearchStore();
      const searches: any[] = [];

      try {
        // Get all profiles in the current dashboard
        const profiles = searchStore.nestedSearches || [];

        // Make ALL requests in parallel across all profiles (2 requests per profile)
        // This is much faster than iterating profiles sequentially
        const allRequests = profiles.flatMap((profile, index) => [
          this.fetchRawChartSearch({
            profileId: profile.id,
            profileSearchText: profile.attributes?.profile_search_text || "",
            dateRange: firstHalf,
            aggregationScale,
            requestId: `dashboard-${index}-first`,
          }),
          this.fetchRawChartSearch({
            profileId: profile.id,
            profileSearchText: profile.attributes?.profile_search_text || "",
            dateRange: secondHalf,
            aggregationScale,
            requestId: `dashboard-${index}-second`,
          }),
        ]);

        const allResults = await Promise.all(allRequests);

        // Import parser once outside the loop
        const { parseChartSearchResults } = await import(
          "~/utils/ppt-reports/chart-search"
        );

        // Pair up results and merge: [0,1] = profile 0, [2,3] = profile 1, etc.
        for (let i = 0; i < profiles.length; i++) {
          const profile = profiles[i];
          const profileName = profile.attributes?.name || "Unknown Profile";
          const firstHalfRaw = allResults[i * 2];
          const secondHalfRaw = allResults[i * 2 + 1];

          const mergedRaw = mergeRawApiResponses(firstHalfRaw, secondHalfRaw);

          if (mergedRaw) {
            const parsedChartSearch = parseChartSearchResults(
              mergedRaw.attributes as any,
              { id: profile.id, name: profileName },
              fullDateRange.start,
              fullDateRange.end,
              aggregationScale,
              searchStore.currentSearchFilter,
            );

            searches.push({
              ...parsedChartSearch,
              profile: { name: profileName },
              datasetName: profileName,
            });
          }
        }
      } catch (error) {
        console.error(
          "PPT Reports: Error fetching dashboard chart searches",
          error,
        );
      }

      return searches;
    },

    /**
     * Fetch raw chart search data without parsing
     * Used for split date range requests that need to be merged before parsing
     * Uses a unique requestId param to prevent useApiCall from aborting parallel requests
     */
    async fetchRawChartSearch(params: {
      profileId: string;
      profileSearchText: string;
      dateRange: { start: Date; end: Date };
      aggregationScale: string;
      requestId: string; // Unique ID to prevent request deduplication/abort
      overrides?: any;
    }) {
      try {
        const requestBody = buildIsolatedSearchRequest({
          searchType: "chart-searches",
          searchText: params.profileSearchText,
          profileId: params.profileId,
          dateRange: params.dateRange,
          querySearchType: 1,
          overrides: {
            aggregation_fields:
              "articles|social|reach|article_sentiment|facebook_shares|pinterest_pins|twitter_tweets",
            aggregation_scale: params.aggregationScale,
            ...params.overrides,
          },
        });

        // Add unique requestId param to prevent useApiCall from deduplicating/aborting
        // parallel requests. This makes each request have a unique cacheKey.
        const options: RequestOptions = {
          method: "POST",
          body: requestBody,
          params: { _pptRequestId: params.requestId },
        };

        const response = await useApiCall("/chart_searches", options);

        if (response?.data?.value?.data) {
          return response.data.value.data;
        }
      } catch (error) {
        console.error("PPT Reports: Error fetching raw chart search", error);
      }

      return null;
    },

    async fetchMainChartSearches(
      firstHalf: { start: Date; end: Date },
      secondHalf: { start: Date; end: Date },
      aggregationScale: string,
    ) {
      const searchStore = useSearchStore();
      const searches: any[] = [];

      // Use selectedProfile or fall back to first nested search
      let mainProfile = searchStore.selectedProfile;

      // If no selectedProfile, use first nested search as fallback
      if (
        !mainProfile &&
        searchStore.nestedSearches &&
        searchStore.nestedSearches.length > 0
      ) {
        mainProfile = searchStore.nestedSearches[0];
      }

      if (!mainProfile) {
        return searches;
      }

      try {
        // First half - normal search
        const firstHalfSearch = await this.createChartSearch({
          profileId: mainProfile.id,
          profileSearchText: mainProfile.attributes?.profile_search_text || "",
          dateRange: firstHalf,
          aggregationScale,
        });
        if (firstHalfSearch) searches.push(firstHalfSearch);

        // Second half - normal search
        const secondHalfSearch = await this.createChartSearch({
          profileId: mainProfile.id,
          profileSearchText: mainProfile.attributes?.profile_search_text || "",
          dateRange: secondHalf,
          aggregationScale,
        });
        if (secondHalfSearch) searches.push(secondHalfSearch);

        // High publication rank searches
        const firstHalfHighRank = await this.createChartSearch({
          profileId: mainProfile.id,
          profileSearchText: mainProfile.attributes?.profile_search_text || "",
          dateRange: firstHalf,
          aggregationScale,
          overrides: { filter_pagerank_min: 8, filter_pagerank_max: 10 },
        });
        if (firstHalfHighRank) searches.push(firstHalfHighRank);

        const secondHalfHighRank = await this.createChartSearch({
          profileId: mainProfile.id,
          profileSearchText: mainProfile.attributes?.profile_search_text || "",
          dateRange: secondHalf,
          aggregationScale,
          overrides: { filter_pagerank_min: 8, filter_pagerank_max: 10 },
        });
        if (secondHalfHighRank) searches.push(secondHalfHighRank);

        // High relevance searches
        const firstHalfHighRelevance = await this.createChartSearch({
          profileId: mainProfile.id,
          profileSearchText: mainProfile.attributes?.profile_search_text || "",
          dateRange: firstHalf,
          aggregationScale,
          overrides: { filter_relevance: 5 },
        });
        if (firstHalfHighRelevance) searches.push(firstHalfHighRelevance);

        const secondHalfHighRelevance = await this.createChartSearch({
          profileId: mainProfile.id,
          profileSearchText: mainProfile.attributes?.profile_search_text || "",
          dateRange: secondHalf,
          aggregationScale,
          overrides: { filter_relevance: 5 },
        });
        if (secondHalfHighRelevance) searches.push(secondHalfHighRelevance);
      } catch (error) {
        console.error("PPT Reports: Error fetching main chart searches", error);
      }

      return searches;
    },

    async fetchCompetitorChartSearches(
      firstHalf: { start: Date; end: Date },
      secondHalf: { start: Date; end: Date },
      aggregationScale: string,
    ) {
      const searchStore = useSearchStore();
      const searches: any[] = [];

      // Use selectedProfile or fall back to first nested search
      let mainProfile = searchStore.selectedProfile;
      if (
        !mainProfile &&
        searchStore.nestedSearches &&
        searchStore.nestedSearches.length > 0
      ) {
        mainProfile = searchStore.nestedSearches[0];
      }

      // Only fetch if we have competitors (comparison set with multiple profiles)
      if (
        !searchStore.selectedSearch?.is_comparison ||
        !searchStore.nestedSearches ||
        searchStore.nestedSearches.length <= 1
      ) {
        return searches;
      }

      try {
        // Get all profiles except the main one
        const competitors = searchStore.nestedSearches.filter(
          (p) => p.id !== mainProfile?.id,
        );

        for (const competitor of competitors) {
          // First half
          const firstHalfSearch = await this.createChartSearch({
            profileId: competitor.id,
            profileSearchText: competitor.attributes?.profile_search_text || "",
            dateRange: firstHalf,
            aggregationScale,
          });
          if (firstHalfSearch) {
            searches.push({
              ...firstHalfSearch,
              profile: { name: competitor.attributes?.name },
              datasetName: competitor.attributes?.name,
            });
          }

          // Second half
          const secondHalfSearch = await this.createChartSearch({
            profileId: competitor.id,
            profileSearchText: competitor.attributes?.profile_search_text || "",
            dateRange: secondHalf,
            aggregationScale,
          });
          if (secondHalfSearch) {
            searches.push({
              ...secondHalfSearch,
              profile: { name: competitor.attributes?.name },
              datasetName: competitor.attributes?.name,
            });
          }
        }
      } catch (error) {
        console.error(
          "PPT Reports: Error fetching competitor chart searches",
          error,
        );
      }

      return searches;
    },

    async createChartSearch(params: {
      profileId: string;
      profileSearchText: string;
      dateRange: { start: Date; end: Date };
      aggregationScale: string;
      overrides?: any;
    }) {
      try {
        // Use isolated request builder - NO global state modification
        const requestBody = buildIsolatedSearchRequest({
          searchType: "chart-searches",
          searchText: params.profileSearchText,
          profileId: params.profileId,
          dateRange: params.dateRange,
          querySearchType: 1, // Chart search type
          overrides: {
            // Set the correct aggregation fields and scale
            aggregation_fields:
              "articles|social|reach|article_sentiment|facebook_shares|pinterest_pins|twitter_tweets",
            aggregation_scale: params.aggregationScale,
            // Include any additional overrides
            ...params.overrides,
          },
        });

        const options: RequestOptions = {
          method: "POST",
          body: requestBody,
        };

        const response = await useApiCall("/chart_searches", options);

        if (response.data.value?.data) {
          const data = response.data.value.data;

          // Use the original parseResults() logic
          // Import the parseChartSearchResults function
          const { parseChartSearchResults } = await import(
            "~/utils/ppt-reports/chart-search"
          );

          const searchStore = useSearchStore();

          // Create profile object
          const profile = {
            id: params.profileId,
            name:
              searchStore.nestedSearches?.find((p) => p.id === params.profileId)
                ?.attributes?.name || "Unknown Profile",
          };

          // Parse the raw API data using the original logic
          const parsedChartSearch = parseChartSearchResults(
            data.attributes as any, // Raw search result from API
            profile,
            params.dateRange.start,
            params.dateRange.end,
            params.aggregationScale,
            searchStore.currentSearchFilter, // Pass current search filter
          );

          return parsedChartSearch;
        }
      } catch (error) {
        console.error("PPT Reports: Error creating chart search", error);
      }

      return null;
    },

    async fetchTopicSearch(
      dateRange: { start: Date; end: Date },
      aggregationScale: string,
    ) {
      const searchStore = useSearchStore();

      // Use selectedProfile or fall back to first nested search
      let mainProfile = searchStore.selectedProfile;
      if (
        !mainProfile &&
        searchStore.nestedSearches &&
        searchStore.nestedSearches.length > 0
      ) {
        mainProfile = searchStore.nestedSearches[0];
      }

      if (!mainProfile) {
        return null;
      }

      try {
        const requestBody = buildIsolatedSearchRequest({
          searchType: "topic-searches",
          searchText: mainProfile.attributes?.profile_search_text || "",
          profileId: mainProfile.id,
          dateRange,
          querySearchType: 1, // Not used for topic-searches, gets deleted in builder
          overrides: {
            aggregation_scale: aggregationScale,
          },
        });

        const options: RequestOptions = {
          method: "POST",
          body: requestBody,
        };

        const response = await useApiCall("/topic_searches", options);

        if (
          response?.data?.value?.data?.attributes?.search_result?.counts
            ?.keyphrases
        ) {
          const keyphrases =
            response.data.value.data.attributes.search_result.counts.keyphrases;

          // Transform keyphrases object to keywords array matching TopicKeyword interface
          const keywords = Object.entries(keyphrases).map(
            ([key, value]: [string, any]) => ({
              datasetName: key,
              name: key,
              mentionTotalCount: value.total || 0,
              mentionCounts:
                value.aggregations?.map((agg: any) => agg.total) || [],
              mentionDates:
                value.aggregations?.map((agg: any) => agg.date) || [],
            }),
          );

          // Sort by total count descending and take top 20
          keywords.sort((a, b) => b.mentionTotalCount - a.mentionTotalCount);

          return {
            keywords: keywords.slice(0, 20),
          };
        }
      } catch (error) {
        console.error("PPT Reports: Error fetching topic search", error);
      }

      return null;
    },

    async fetchArticleSearches(dateRange: { start: Date; end: Date }) {
      const searchStore = useSearchStore();
      const mostAmplified: any[] = [];

      // Use selectedProfile or fall back to first nested search
      let mainProfile = searchStore.selectedProfile;
      if (
        !mainProfile &&
        searchStore.nestedSearches &&
        searchStore.nestedSearches.length > 0
      ) {
        mainProfile = searchStore.nestedSearches[0];
      }

      if (!mainProfile) {
        return { mostAmplified };
      }

      try {
        // Use isolated request builder - NO global state modification
        const amplifiedQuery = buildIsolatedSearchRequest({
          searchType: "article-searches",
          searchText: mainProfile.attributes?.profile_search_text || "",
          profileId: mainProfile.id,
          dateRange,
          querySearchType: 0, // Article search type
        });

        const amplifiedOptions: RequestOptions = {
          method: "POST",
          body: amplifiedQuery,
        };
        const amplifiedResponse = await useApiCall(
          "/article_searches",
          amplifiedOptions,
        );

        if (
          amplifiedResponse.data.value?.data?.attributes?.search_result
            ?.articles
        ) {
          const articles =
            amplifiedResponse.data.value.data.attributes.search_result.articles;
          mostAmplified.push(...articles.slice(0, 5));
        }
      } catch (error) {
        console.error("PPT Reports: Error fetching article searches", error);
      }

      return { mostAmplified };
    },

    fetchOrgData() {
      const orgStore = useOrgStore();

      this.reportData.orgData = {
        name: orgStore.getOrg?.attributes?.name || "Organization",
        timezone: orgStore.getTimeZone,
        features: orgStore.getFeatures,
      };
    },

    async fetchOrgLogo() {
      try {
        const orgStore = useOrgStore();
        const logoId = orgStore.getOrg?.attributes?.logo_id;

        if (logoId) {
          const options: RequestOptions = { method: "GET" };
          const response = await useApiCall(`/uploads/${logoId}`, options);

          if (response.data.value?.data?.attributes?.url) {
            this.reportData.logoUrl = response.data.value.data.attributes.url;
          }
        }
      } catch (error) {
        console.warn("PPT Reports: Could not fetch logo", error);
        // Logo is optional, don't throw error
      }
    },

    async generatePowerPointReport(
      t?: (key: string, options?: Record<string, unknown>) => string,
    ) {
      this.isGenerating = true;
      this.error = null;

      try {
        const { generatePptReport } = await import(
          "~/utils/ppt-reports/ppt-exporter"
        );

        this.updateProgress("Initializing report generation...", 1, 6);

        // Ensure we have fresh data
        if (!this.isReady) {
          this.updateProgress("Fetching report data...", 2, 6);
          await this.fetchReportData();
        }

        this.updateProgress("Preparing slides...", 3, 6);

        const reportConfig = {
          title: this.reportSettings.title,
          dateRange: this.reportSettings.dateRange,
          selectedSlides: this.selectedSlides,
          data: this.reportData,
          settings: this.reportSettings,
          topicChartImage: this.topicChartImage,
          t,
        };

        this.updateProgress("Generating PowerPoint...", 4, 6);

        await generatePptReport(
          reportConfig,
          (step: string, current: number, total: number) => {
            this.updateProgress(step, current + 4, total + 4);
          },
        );

        this.updateProgress("Download complete!", 6, 6);

        // Reset progress after a short delay
        setTimeout(() => {
          this.generationProgress = {
            currentStep: "",
            totalSteps: 0,
            currentStepNumber: 0,
          };
        }, 2000);
      } catch (error) {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to generate PowerPoint report";
        console.error("PPT Reports: Generation failed", error);
      } finally {
        this.isGenerating = false;
      }
    },

    reset() {
      this.isLoading = false;
      this.isGenerating = false;
      this.error = null;
      this.reportData = {
        searchData: null,
        chartData: null,
        orgData: null,
        logoUrl: null,
      };
      this.reportStats = null;
      this.topicChartImage = null;
      this.generationProgress = {
        currentStep: "",
        totalSteps: 0,
        currentStepNumber: 0,
      };
    },
  },
});
